import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { useUsers } from "../../context/UsersContext";
import { useEventos } from "../../context/EventosContext";
import { useNotas } from "../../context/NotasContext";
import ReactQuill from "react-quill";
import { FaSpinner } from "react-icons/fa"; // Ícono de carga

const Modal = ({ isOpen, onClose, evento, showNotification }) => {
  const { user } = useAuth();
  const { salones, asignaturas, fetchSalones, fetchAsignaturas, salonGrado } = useUsers();
  const { addEvento } = useEventos();
  const { getAsignaturasSalon, asignaturasSalon, loadingAsignaturas } = useNotas();
  
  // ✅ AGREGAR: Referencia para ReactQuill
  const quillRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false); // Nuevo estado de carga
  const [showSalonesDropdown, setShowSalonesDropdown] = useState(false);
  const salonesDropdownRef = useRef(null);

  const [formData, setFormData] = useState({
    titulo: "",
    fecha: "",
    descripcion: "",
    salon_id: user.rol_id === 2 ? "" : null, // Profesor: single select
    salon_ids: [], // Rector: multi-select
    asignatura_id: user.rol_id === 2 ? "" : null,
    es_evento_general: user.rol_id === 1, // Por defecto es general para rector
    fotos: [],
  });

  useEffect(() => {
    if (evento) {
      setFormData({
        titulo: evento.titulo,
        fecha: evento.fecha,
        descripcion: evento.descripcion,
        salon_id: evento.salon_id || "",
        asignatura_id: evento.asignatura_id || "",
        es_evento_general: evento.es_evento_general,
      });
    }
  }, [evento]);

  // ✅ NUEVO: Cargar contenido HTML en Quill cuando hay descripción
  useEffect(() => {
    if (isOpen && quillRef.current && formData.descripcion) {
      // Esperar un tick para asegurar que Quill está montado
      const timer = setTimeout(() => {
        const editor = quillRef.current?.getEditor?.();
        if (editor) {
          editor.clipboard.dangerouslyPasteHTML(formData.descripcion);
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, evento, formData.descripcion]);

  // Cerrar dropdown de salones al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (salonesDropdownRef.current && !salonesDropdownRef.current.contains(e.target)) {
        setShowSalonesDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [previewImages, setPreviewImages] = useState([]);
  const maxLength = 2000;

  useEffect(() => {
    if (isOpen) {
      // 🔥 CORREGIDO: Cargar salones para RECTOR y PROFESOR
      if ((user.rol_id === 1 || user.rol_id === 2)) {
        if (salones.length === 0) fetchSalones();
        // ✅ ELIMINAR: if (asignaturas.length === 0) fetchAsignaturas();
      }
    }
  }, [
    isOpen,
    user.rol_id,
    fetchSalones,
    salones.length,
  ]);

  // ✅ MODIFICADO: Cargar asignaturas cuando se seleccione salón
  useEffect(() => {
    const cargarAsignaturas = async () => {
      // Para rector: solo cargar si tiene exactamente 1 salón seleccionado
      let salonId;
      if (user.rol_id === 1) {
        salonId = formData.salon_ids.length === 1 ? formData.salon_ids[0] : null;
      } else {
        salonId = formData.salon_id;
      }
      
      if (!salonId) return;

      try {
        await getAsignaturasSalon(salonId);
      } catch (error) {
        console.error("Error cargando asignaturas:", error);
      }
    };

    if (isOpen) {
      cargarAsignaturas();
    }
  }, [isOpen, formData.salon_id, formData.salon_ids, getAsignaturasSalon, user.rol_id]);

  // Toggle salón en la lista de seleccionados (rector multi-select)
  const handleToggleSalon = (salonId) => {
    setFormData((prev) => {
      const ids = prev.salon_ids.includes(salonId)
        ? prev.salon_ids.filter((id) => id !== salonId)
        : [...prev.salon_ids, salonId];
      return {
        ...prev,
        salon_ids: ids,
        asignatura_id: ids.length === 1 ? prev.asignatura_id : "",
        es_evento_general: ids.length === 0,
      };
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "salon_id") {
      const esGeneral = user.rol_id === 1 && (!value || value === "");
      
      setFormData((prev) => ({ 
        ...prev, 
        [name]: value || null,
        asignatura_id: "", // ✅ RESETEAR asignatura al cambiar salón
        es_evento_general: esGeneral
      }));
      
    } else {
      setFormData((prev) => ({ 
        ...prev, 
        [name]: value
      }));
    }
  };

  // ✅ MODIFICAR: Función para obtener grado del salón
  const getSalonGrado = () => {
    // Para rector con multi-select: solo mostrar asignaturas si hay exactamente 1 salón
    if (user.rol_id === 1 && formData.salon_ids.length === 1) {
      const salon = salones.find(s => s.id === formData.salon_ids[0]);
      return salon?.grado?.toLowerCase();
    }
    // Para profesor: usar salon_id
    if (formData.salon_id) {
      const salon = salones.find(s => s.id === parseInt(formData.salon_id));
      return salon?.grado?.toLowerCase();
    }
    return null;
  };

  const gradoActual = getSalonGrado();
  const esPreescolar = ["parvulos", "prejardin", "jardin", "transicion"].includes(gradoActual);

  const handleDescripcionChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      descripcion: value,
    }));
  };


  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFormData((prev) => ({ ...prev, fotos: [...prev.fotos, ...newFiles] }));

    const newPreviews = newFiles.map((file) => URL.createObjectURL(file));
    setPreviewImages((prev) => [...prev, ...newPreviews]);
  };

  const buildFormData = (formDataObj, user, overrideSalonId = null) => {
    const data = new FormData();
    data.append("titulo", formDataObj.titulo);
    data.append("fecha", formDataObj.fecha);
    data.append("descripcion", formDataObj.descripcion);

    const salonId = overrideSalonId || formDataObj.salon_id;

    if (salonId) {
      data.append("salon_id", salonId);
    }
    
    if (formDataObj.asignatura_id) {
      data.append("asignatura_id", formDataObj.asignatura_id);
    }

    const esEventoGeneral = salonId ? false : (user.rol_id === 1);
    data.append("es_evento_general", esEventoGeneral);

    formDataObj.fotos.forEach((foto) => data.append("fotos", foto));

    return data;
  };

  const resetForm = () => {
    setFormData({
      titulo: "",
      fecha: "",
      descripcion: "",
      salon_id: user.rol_id === 2 ? "" : null,
      salon_ids: [],
      asignatura_id: user.rol_id === 2 ? "" : null,
      es_evento_general: user.rol_id === 1,
      fotos: [],
    });
    setPreviewImages([]);
    setShowSalonesDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Rector con múltiples salones seleccionados
      if (user.rol_id === 1 && formData.salon_ids.length > 0) {
        for (const salonId of formData.salon_ids) {
          const data = buildFormData(formData, user, salonId);
          await addEvento(data);
        }
        showNotification({
          type: "success",
          message: `Evento creado en ${formData.salon_ids.length} salón(es) correctamente.`,
        });
      } else {
        // Profesor (single salon) o rector sin salón (general)
        const data = buildFormData(formData, user);
        await addEvento(data);
        showNotification({
          type: "success",
          message: "Evento creado con éxito.",
        });
      }

      resetForm();
      onClose();
    } catch (error) {
      showNotification({
        type: "error",
        message: "Error al crear el evento. Inténtalo de nuevo.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
      <div className="bg-white rounded-lg shadow-lg w-11/12 max-w-2xl">
        <div className="bg-main-bg-sidebar text-white flex justify-between items-center px-6 py-2 rounded-t-lg">
          <h2 className="text-lg font-bold">Agregar Evento</h2>
          <button
            onClick={onClose}
            className="text-white text-2xl font-bold hover:text-gray-200"
          >
            &times;
          </button>
        </div>

        <div className="p-6 overflow-auto max-h-[80vh]">
          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <label className="block text-sm font-bold mb-2 text-blue-900">
                Título Evento*
              </label>
              <input
                type="text"
                name="titulo"
                value={formData.titulo}
                onChange={handleInputChange}
                required
                placeholder="Actividad"
                className="w-full px-3 py-2 border rounded focus:outline-blue-800"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-bold mb-2 text-blue-900">
                Fecha Actividad *
              </label>
              <input
                type="date"
                name="fecha"
                value={formData.fecha}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border rounded focus:outline-blue-800"
              />
            </div>

            {/* Selector de salón para RECTOR y PROFESOR */}
            {(user.rol_id === 1 || user.rol_id === 2) && (
              <>
                <div className="mb-6">
                  <label className="block text-sm font-bold mb-2 text-blue-900">
                    {user.rol_id === 1 ? "Salones (Opcional - puedes seleccionar varios)" : "Salón *"}
                  </label>

                  {/* RECTOR: Multi-select con checkboxes */}
                  {user.rol_id === 1 ? (
                    <div className="relative" ref={salonesDropdownRef}>
                      <button
                        type="button"
                        onClick={() => setShowSalonesDropdown(!showSalonesDropdown)}
                        className="w-full px-3 py-2 border rounded focus:outline-blue-800 bg-white text-left flex justify-between items-center"
                      >
                        <span className={formData.salon_ids.length === 0 ? "text-gray-500" : "text-gray-800"}>
                          {formData.salon_ids.length === 0
                            ? "Todo el colegio (General)"
                            : `${formData.salon_ids.length} salón(es) seleccionado(s)`}
                        </span>
                        <svg className={`w-4 h-4 transition-transform ${showSalonesDropdown ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {showSalonesDropdown && (
                        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {/* Opción para deseleccionar todos */}
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, salon_ids: [], asignatura_id: "", es_evento_general: true }))}
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 flex items-center gap-2 border-b ${
                              formData.salon_ids.length === 0 ? "bg-blue-50 text-blue-700 font-semibold" : "text-gray-700"
                            }`}
                          >
                            <span className="w-4 h-4 border rounded flex items-center justify-center text-xs">
                              {formData.salon_ids.length === 0 && "✓"}
                            </span>
                            📢 Todo el colegio (General)
                          </button>

                          {/* Seleccionar / Deseleccionar todos */}
                          <button
                            type="button"
                            onClick={() => {
                              const allIds = salones.map(s => s.id);
                              const todosSeleccionados = formData.salon_ids.length === salones.length;
                              setFormData(prev => ({
                                ...prev,
                                salon_ids: todosSeleccionados ? [] : allIds,
                                asignatura_id: "",
                                es_evento_general: todosSeleccionados,
                              }));
                            }}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 border-b text-blue-600 font-medium"
                          >
                            <span className="w-4 h-4 border rounded flex items-center justify-center text-xs">
                              {formData.salon_ids.length === salones.length && "✓"}
                            </span>
                            {formData.salon_ids.length === salones.length ? "Deseleccionar todos" : "Seleccionar todos"}
                          </button>

                          {salones.map((salon) => (
                            <button
                              type="button"
                              key={salon.id}
                              onClick={() => handleToggleSalon(salon.id)}
                              className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 flex items-center gap-2 ${
                                formData.salon_ids.includes(salon.id) ? "bg-blue-50 text-blue-700" : "text-gray-700"
                              }`}
                            >
                              <span className={`w-4 h-4 border rounded flex items-center justify-center text-xs ${
                                formData.salon_ids.includes(salon.id) ? "bg-blue-600 text-white border-blue-600" : "border-gray-300"
                              }`}>
                                {formData.salon_ids.includes(salon.id) && "✓"}
                              </span>
                              {salon.nombre} - {salon.grado}
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Tags de salones seleccionados */}
                      {formData.salon_ids.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {formData.salon_ids.map((id) => {
                            const salon = salones.find(s => s.id === id);
                            return salon ? (
                              <span key={id} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                                {salon.nombre}
                                <button
                                  type="button"
                                  onClick={() => handleToggleSalon(id)}
                                  className="hover:text-red-500 font-bold"
                                >
                                  ×
                                </button>
                              </span>
                            ) : null;
                          })}
                        </div>
                      )}

                      {formData.salon_ids.length === 0 && (
                        <p className="text-xs text-blue-600 mt-1">
                          📢 El evento se enviará a TODO el colegio
                        </p>
                      )}
                      {formData.salon_ids.length > 0 && (
                        <p className="text-xs text-gray-600 mt-1">
                          ℹ️ Se creará el evento en {formData.salon_ids.length} salón(es)
                        </p>
                      )}
                    </div>
                  ) : (
                    /* PROFESOR: Single select */
                    <select
                      name="salon_id"
                      value={formData.salon_id || ""}
                      onChange={handleInputChange}
                      required={user.rol_id === 2}
                      className="w-full px-3 py-2 border rounded focus:outline-blue-800"
                    >
                      <option value="">Seleccione un salón</option>
                      {salones.map((salon) => (
                        <option key={salon.id} value={salon.id}>
                          {salon.nombre} - {salon.grado}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Mostrar asignaturas solo si hay UN salón seleccionado */}
                {((user.rol_id === 2 && formData.salon_id) || (user.rol_id === 1 && formData.salon_ids.length === 1)) && (
                  <div className="mb-6">
                    <label className="block text-sm font-bold mb-2 text-blue-900">
                      Asignatura {user.rol_id === 2 ? "" : "(Opcional)"}
                    </label>
                    <select
                      name="asignatura_id"
                      value={formData.asignatura_id || ""}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded focus:outline-blue-800"
                    >
                      <option value="">
                        {loadingAsignaturas ? "Cargando asignaturas..." : "Seleccione una asignatura"}
                      </option>
                      {asignaturasSalon
                        .filter((asignatura) => {
                          // ✅ USAR LÓGICA DE FILTRADO IGUAL QUE EN NotasForm
                          if (esPreescolar) {
                            return asignatura.descripcion && asignatura.descripcion.trim() !== "";
                          } else {
                            return asignatura.IH && !isNaN(parseInt(asignatura.IH)) && parseInt(asignatura.IH) > 0;
                          }
                        })
                        .sort((a, b) => {
                          if (esPreescolar) {
                            return a.nombre.localeCompare(b.nombre);
                          } else {
                            const ihDiff = parseInt(b.IH || 0) - parseInt(a.IH || 0);
                            return ihDiff !== 0 ? ihDiff : a.nombre.localeCompare(b.nombre);
                          }
                        })
                        .map((asignatura) => (
                          <option key={asignatura.asignatura_id} value={asignatura.asignatura_id}>
                            {asignatura.nombre}
                          </option>
                        ))}
                    </select>
                  </div>
                )}
              </>
            )}

            <div className="mb-6">
              <label className="block text-sm font-bold mb-2 text-blue-900">
                Fotos *
              </label>
              <input
                type="file"
                name="fotos"
                multiple
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="text-gray cursor-pointer border-2 border-dashed border-gray rounded-lg p-6 w-full text-center focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-center"
              >
                Subir
              </label>

              {/* Previsualización de imágenes seleccionadas */}
              <div className="flex space-x-4 mt-4 flex-wrap">
                {previewImages.map((src, index) => (
                  <div key={index} className="relative">
                    <img
                      src={src}
                      alt={`Preview ${index + 1}`}
                      className="w-20 h-20 object-cover rounded shadow"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        // Eliminar imagen de preview y formData.fotos
                        setPreviewImages((prev) =>
                          prev.filter((_, i) => i !== index)
                        );
                        setFormData((prev) => ({
                          ...prev,
                          fotos: prev.fotos.filter((_, i) => i !== index),
                        }));
                      }}
                      className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full px-2 py-1 hover:bg-red-600"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-bold mb-2 text-blue-900">
                Descripción *
              </label>
              {/* <textarea
                name="descripcion"
                value={formData.descripcion}
                onChange={handleInputChange}
                required
                maxLength={maxLength}
                rows="4"
                placeholder="Descripción..."
                className="w-full px-3 py-2 border rounded focus:outline-blue-800"
              ></textarea> */}

              <ReactQuill
                name="descripcion"
                value={formData.descripcion}
                onChange={handleDescripcionChange}
                required
                maxLength={maxLength}
                // rows="4"
                className="w-full px-3 py-2 border rounded focus:outline-blue-800"
                modules={{
                  toolbar: [
                    [{ list: "ordered" }, { list: "bullet" }],
                    ["bold", "italic", "underline"],
                    ["link"],
                    ["clean"],
                  ],
                }}
              />
              <div className="text-right text-gray-500 text-sm">
                {formData.descripcion.length}/{maxLength}
              </div>
            </div>

            <div className="text-right">
              <button
                type="submit"
                className="bg-main-bg-sidebar text-white px-6 py-2 rounded-lg hover:bg-blue-800 flex items-center justify-center"
                disabled={isLoading} // Deshabilitar botón si está en carga
              >
                {isLoading ? (
                  <FaSpinner className="animate-spin mr-2" />
                ) : (
                  "Guardar"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Modal;
